// /mnt/data/Pasted code.js
import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaGraduationCap,
  FaBriefcase,
  FaGlobe,
  FaChartLine,
  FaHandshake,
} from "react-icons/fa";

const milestones = [
  {
    year: "2011–2012",
    title: "Master’s in Geohydrology",
    description:
      "Exploring the flow of water, learning the value of long-term systems thinking.",
    icon: <FaGraduationCap size={24} />,
    image: "/images/geohydrology.jpg",
    details:
      "Completed a master's degree focusing on geohydrology and water systems. Research included fieldwork in rivers and aquifers, developing expertise in sustainable water management.",
  },
  {
    year: "2013–2015",
    title: "Environmental Specialist, Glencore",
    description:
      "Early professional experiences grounding me in environmental responsibility.",
    icon: <FaBriefcase size={24} />,
    image: "/images/KenzoGlencore.jpeg",
    details:
      "Worked on environmental compliance and sustainability projects, implementing monitoring and reporting systems to reduce corporate impact on local ecosystems.",
  },
  {
    year: "2015–2019",
    title: "Sasol – Safety & Environmental Specialist / Lean Six Sigma Blackbelt",
    description:
      "Applying discipline and process improvement principles, lessons in endurance akin to long-distance running.",
    icon: <FaChartLine size={24} />,
    image: "/images/sasol.jpg",
    details:
      "Led Lean Six Sigma initiatives to optimize processes and enhance safety standards, reducing operational risks and improving efficiency across teams.",
  },
  {
    year: "2020–2023",
    title: "ESG Specialist & Policy Support, NDB, Shanghai",
    description:
      "Global exposure to sustainable finance, navigating complex international systems.",
    icon: <FaGlobe size={24} />,
    image: "/images/ndb-shanghai.jpg",
    details:
      "Supported ESG projects and policy development at the New Development Bank, collaborating internationally to integrate sustainability into investment and finance frameworks.",
  },
  {
    year: "2023–Present",
    title:
      "Director & Head: ESG Commercialization & Stakeholder Engagement, Absa Group",
    description:
      "Integrating sustainability into business strategy, unlocking commercial value through responsible stewardship.",
    icon: <FaHandshake size={24} />,
    image: "/images/KenAbsa.jpeg",
    details:
      "Leading ESG commercialization initiatives, coordinating stakeholder engagement, and embedding responsible practices into strategic business growth plans.",
  },
];

const Timeline = () => {
  const [selectedMilestone, setSelectedMilestone] = useState(null);

  return (
    <section className="relative overflow-hidden bg-white px-6 py-24 text-black md:px-20">
      <div className="mb-16 text-center">
        <h2 className="animate-fadeIn text-3xl font-bold md:text-4xl">
          My Journey
        </h2>
        <p className="animate-fadeIn delay-200 mx-auto mt-4 max-w-3xl text-black">
          Every step, challenge, and milestone has shaped my journey; blending
          education, professional experience, and personal growth into
          meaningful impact.
        </p>
      </div>

      <div className="relative">
        <div className="absolute left-1/2 h-full w-1 -translate-x-1/2 transform bg-black" />

        <div className="space-y-16">
          {milestones.map((milestone, index) => (
            <motion.div
              key={milestone.title}
              initial={{ opacity: 0, x: index % 2 === 0 ? -100 : 100 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className={`relative md:flex md:items-center ${
                index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
              }`}
            >
              <div
                className="relative z-10 cursor-pointer rounded-xl border border-black bg-white p-6 shadow-md transition hover:scale-105 hover:shadow-xl md:w-1/2"
                onClick={() => setSelectedMilestone(milestone)}
              >
                <div className="mb-2 flex items-center gap-4 text-black">
                  {milestone.icon}
                  <span className="font-bold">{milestone.year}</span>
                </div>
                <h3 className="mb-2 text-xl font-semibold">{milestone.title}</h3>
                <p className="text-black">{milestone.description}</p>
              </div>

              <div className="relative flex justify-center md:w-1/2">
                <div className="z-20 mt-2 h-6 w-6 rounded-full border-4 border-white bg-black shadow-lg md:mt-0" />
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {selectedMilestone && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 px-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedMilestone(null)}
          >
            <motion.div
              className="relative w-full max-w-xl rounded-xl bg-white p-8 text-black shadow-2xl"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setSelectedMilestone(null)}
                className="absolute right-4 top-4 text-2xl font-bold text-black transition hover:opacity-70"
                aria-label="Close modal"
              >
                &times;
              </button>

              {selectedMilestone.image && (
                <img
                  src={selectedMilestone.image}
                  alt={selectedMilestone.title}
                  className="mb-6 h-64 w-full rounded-lg object-contain"
                />
              )}

              <h3 className="mb-4 text-2xl font-bold">
                {selectedMilestone.title}
              </h3>
              <span className="mb-4 block font-semibold">
                {selectedMilestone.year}
              </span>
              <p>{selectedMilestone.details}</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Timeline;